import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import InlineLinkActionButton from 'components/generic/InlineButton/InlineLinkActionButton';
import InlinePageActionButton from 'components/generic/InlineButton/InlinePageActionButton';
import ComponentRenderer from '../../shared/ComponentRenderer';
import { navigationLinkToPageAction } from '../../shared/ContentfulHelpers';
import { StyledBody, StyledHeadline, StyledTitle } from './RichTextStyledTypography';

export default function RichText(document: Document) {
    const richTextOptions = {
        renderText: (text) => {
            return text.split('\n').reduce((children, textSegment, index) => {
                return [...children, index > 0 && <br key={index} />, textSegment];
            }, []);
        },
        renderMark: {
            [MARKS.BOLD]: (text) => <b>{text}</b>,
            [MARKS.ITALIC]: (text) => <i>{text}</i>,
            [MARKS.STRIKETHROUGH]: (text) => <s>{text}</s>,
            [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
            [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
            [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
            [MARKS.CODE]: (text) => <code>{text}</code>
        },
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => <StyledBody>{children}</StyledBody>,
            [BLOCKS.HEADING_1]: (node, children) => (
                <StyledHeadline size="L" forwardedAs="h1">
                    {children}
                </StyledHeadline>
            ),
            [BLOCKS.HEADING_2]: (node, children) => (
                <StyledHeadline size="M" forwardedAs="h2">
                    {children}
                </StyledHeadline>
            ),
            [BLOCKS.HEADING_3]: (node, children) => (
                <StyledHeadline size="S" forwardedAs="h3">
                    {children}
                </StyledHeadline>
            ),
            [BLOCKS.HEADING_4]: (node, children) => (
                <StyledTitle size="L" forwardedAs="div">
                    {children}
                </StyledTitle>
            ),
            [BLOCKS.HEADING_5]: (node, children) => (
                <StyledTitle size="M" forwardedAs="div">
                    {children}
                </StyledTitle>
            ),
            [BLOCKS.HEADING_6]: (node, children) => (
                <StyledTitle size="S" forwardedAs="div">
                    {children}
                </StyledTitle>
            ),
            [INLINES.HYPERLINK]: (node, children) => (
                <InlineLinkActionButton
                    action={{ $type: 'PlainLinkAction', url: node.data.uri, noFollow: false, openInNewWindow: false }}
                >
                    {children}
                </InlineLinkActionButton>
            ),
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
                const linkAction = navigationLinkToPageAction({ ...node.data.target });

                if (!linkAction) return children;

                return <InlinePageActionButton action={linkAction}>{children}</InlinePageActionButton>;
            },
            [INLINES.EMBEDDED_ENTRY]: (node) => {
                return <ComponentRenderer {...node.data.target} />;
            }
        }
    };

    return documentToReactComponents(document, richTextOptions);
}
